import type { TFunction } from 'i18next';

import { ROUTE } from '@/nova/routes';

import { isAffiliateCampaignNo } from './affiliate-campaign';
import { assetPath, trackingKindLabel } from './components/LandingPageComposer/link-for';
import type { TrackingLink, TrackingLinkKind } from './tracking-links-api';

/**
 * Where a player (or a stats group) came from, read from the `campaign` value signup stored for them.
 *
 * Signup splits the link's `aff_id` on `_` and keeps the middle part as the player's `campaign`, which is exactly a
 * tracking link's `campaign` token (`tracking-links-api.ts`). So a player's campaign resolves to one link, hence one
 * asset version and, when the link goes through one, one landing page. Other values:
 *  - '' / `direct` / `organic` / `default`: no tracked link (typed the address, search, a plain link);
 *  - an all-digits value: a link minted on the affiliates platform before Studio minted its own (or a real affiliate's
 *    campaign), shown as "Affiliate campaign #<n>" and opened on the Affiliates page;
 *  - anything else: a campaign the brand's links do not know (another tool's link), shown as is.
 * Pure: the caller passes the brand's links (see `useBrandLinks`).
 */
export type Attribution =
  | { kind: 'link'; link: TrackingLink }
  | { kind: 'affiliate'; campaignNo: string }
  | { kind: 'organic' }
  | { kind: 'direct' }
  | { kind: 'other'; campaign: string };

/** Campaign values signup writes when there is no tracked link (quasar's defaults, plus blank). */
const DIRECT_VALUES = new Set(['', 'direct', 'default']);

/** The brand's links keyed by their `campaign` token. Pure. */
export const linksByCampaign = (links: readonly TrackingLink[]): Map<string, TrackingLink> => {
  const out = new Map<string, TrackingLink>();
  links.forEach((l) => {
    if (l.campaign) out.set(l.campaign, l);
  });
  return out;
};

/** Resolve a stored campaign value. Pure. */
export const resolveAttribution = (
  campaign: string | null | undefined,
  byCampaign: ReadonlyMap<string, TrackingLink>,
): Attribution => {
  const value = (campaign ?? '').trim();
  if (value.toLowerCase() === 'organic') return { kind: 'organic' };
  if (DIRECT_VALUES.has(value.toLowerCase())) return { kind: 'direct' };
  const link = byCampaign.get(value);
  if (link) return { kind: 'link', link };
  if (isAffiliateCampaignNo(value)) return { kind: 'affiliate', campaignNo: value };
  return { kind: 'other', campaign: value };
};

/** The asset's display name: its current title (falls back to the link's name, "<title> · <version time>"). Pure. */
export const linkAssetName = (link: TrackingLink): string => link.asset?.name?.trim() || link.name;

/** "Video ad", "Banner kit", … for a link's asset. */
export const linkKindLabel = (link: TrackingLink, t: TFunction): string => trackingKindLabel(link.kind, t);

/** The link's asset composer. Pure. */
export const linkAssetPath = (link: TrackingLink): string => assetPath(link.kind, link.creative_id);

/** The landing page a link goes through (its composer + name), when it lands on a Studio page; a landing page's own
 *  link is the page itself. Pure. */
export const linkLandingPage = (link: TrackingLink): { id: string; name: string; path: string } | undefined => {
  if (link.kind === 'landing_page') {
    return { id: link.creative_id, name: linkAssetName(link), path: ROUTE.creativesLandingPage(link.creative_id) };
  }
  const lp = link.landing_page;
  if (!lp?.id) return undefined;
  return { id: lp.id, name: lp.name, path: ROUTE.creativesLandingPage(lp.id) };
};

/** The Affiliates page, where affiliates-platform campaigns live. With an affiliate's platform id it opens that
 *  affiliate's sheet there (`?affiliate=`). */
export const affiliatesPath = (affiliateId?: string): string =>
  affiliateId ? `${ROUTE.affiliates()}?affiliate=${encodeURIComponent(affiliateId)}` : ROUTE.affiliates();

/** The query keys of the Players page filter by attribution: one link's campaign, or every link landing on a page. */
export const PLAYERS_FROM_PARAMS = {
  /** A tracking link's `campaign` token (or an affiliates-platform campaign number). */
  campaign: 'campaign',
  /** A Studio landing page id: players from every link landing there, the page's own link included. */
  landingPage: 'landing_page',
} as const;

/** The Players page filtered to one link's players. Pure. */
export const playersFromCampaignPath = (campaign: string): string =>
  `${ROUTE.players()}?${PLAYERS_FROM_PARAMS.campaign}=${encodeURIComponent(campaign)}`;

/** The Players page filtered to everyone who came through a landing page. Pure. */
export const playersFromLandingPagePath = (landingPageId: string): string =>
  `${ROUTE.players()}?${PLAYERS_FROM_PARAMS.landingPage}=${encodeURIComponent(landingPageId)}`;

/** Whether a link lands on a Studio page: the page's own link, or an asset's link pointed at it. Pure. */
const landsOn = (link: TrackingLink, landingPageId: string): boolean =>
  (link.kind === 'landing_page' && link.creative_id === landingPageId) || link.landing_page_id === landingPageId;

/** The campaign tokens of every link landing on a Studio page (its own link included). Pure. */
export const campaignsLandingOn = (links: readonly TrackingLink[], landingPageId: string): string[] =>
  links.filter((l) => l.campaign && landsOn(l, landingPageId)).map((l) => l.campaign);

/**
 * The links whose results count for a marketing asset, each with a campaign: a landing page's are every link landing
 * on it (its own link included, see {@link campaignsLandingOn}); any other asset's are its own, one per version (keyed
 * on `creative_id` alone, which is unique across kinds). Pure.
 */
export const assetLinks = (
  links: readonly TrackingLink[],
  kind: TrackingLinkKind,
  creativeId: string,
): TrackingLink[] =>
  links.filter((l) => l.campaign && (kind === 'landing_page' ? landsOn(l, creativeId) : l.creative_id === creativeId));

/** Each listed asset's link campaigns (see {@link assetLinks}); an asset with no link has no entry. Pure. */
export const campaignsByAsset = (
  links: readonly TrackingLink[],
  kind: TrackingLinkKind,
  ids: readonly string[],
): Map<string, Set<string>> => {
  const out = new Map<string, Set<string>>();
  if (kind === 'landing_page') {
    for (const id of ids) {
      const campaigns = campaignsLandingOn(links, id);
      if (campaigns.length > 0) out.set(id, new Set(campaigns));
    }
    return out;
  }
  const wanted = new Set(ids);
  for (const l of links) {
    if (!l.campaign || !wanted.has(l.creative_id)) continue;
    const set = out.get(l.creative_id) ?? new Set<string>();
    set.add(l.campaign);
    out.set(l.creative_id, set);
  }
  return out;
};
